// Location + stream query/mutation hooks (locations.rs).
//   GET/POST /v1/workspaces/{ws}/locations, PATCH /v1/locations/{id}, PATCH /v1/streams/{id}
//
// Neither patch_location nor patch_stream is scoped by workspaceId on the wire
// (the path carries only the location/stream id), so callers pass workspaceId
// alongside the mutated id purely for cache invalidation.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "./http";
import { locationKeys } from "./keys";
import { STATUS_REFRESH_MS } from "./status";
import type {
    CreatedLocation,
    CreateLocation,
    LocationListEntry,
    PatchLocation,
    PatchStream,
    ServiceConfigDocument,
    StreamInfo,
} from "./types";

export function useLocations(workspaceId: string) {
    return useQuery({
        queryKey: locationKeys.list(workspaceId),
        queryFn: () => http.get<LocationListEntry[]>(`/v1/workspaces/${workspaceId}/locations`),
        enabled: Boolean(workspaceId),
        // Service health rides on this list, so it follows the same liveness
        // policy as the Overview's status poll — otherwise the two surfaces
        // can disagree about whether an service is online.
        staleTime: 0,
        refetchInterval: STATUS_REFRESH_MS,
    });
}

export function useCreateLocation(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateLocation) =>
            http.post<CreatedLocation>(`/v1/workspaces/${workspaceId}/locations`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: locationKeys.list(workspaceId) });
        },
    });
}

export function usePatchLocation(workspaceId: string, locationId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        // Answers with the same LocationListEntry shape the list serves — one
        // save re-seeds the whole page, stream half included.
        mutationFn: (body: PatchLocation) =>
            http.patch<LocationListEntry>(`/v1/locations/${locationId}`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: locationKeys.list(workspaceId) });
        },
    });
}

export function useServiceConfig(workspaceId: string, locationId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        // POST because it mints a credential — a mutation, not a read.
        mutationFn: () => http.post<ServiceConfigDocument>(`/v1/locations/${locationId}/service-config`),
        onSuccess: () => {
            // The first download binds the service account, so the list's
            // view of this location is now stale.
            queryClient.invalidateQueries({ queryKey: locationKeys.list(workspaceId) });
        },
    });
}

export function usePatchStream(workspaceId: string, streamId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: PatchStream) => http.patch<StreamInfo>(`/v1/streams/${streamId}`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: locationKeys.list(workspaceId) });
        },
    });
}
